import { useState, useEffect } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Users, Crown, Loader2, Handshake } from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Avatar } from "@/components/shared";
import { API } from "@/utils/constants";

const PLAN_STYLE = {
  creator: "bg-purple-500/10 text-purple-600 border-purple-200",
  pro: "bg-amber-500/10 text-amber-700 border-amber-200",
  pro_monthly: "bg-amber-500/10 text-amber-700 border-amber-200",
  comped: "bg-emerald-500/10 text-emerald-700 border-emerald-200",
  trial: "bg-blue-500/10 text-blue-700 border-blue-200",
  free: "bg-gray-500/10 text-gray-600 border-gray-200",
};

export const AdminUsersView = () => {
  const [users, setUsers] = useState(null);

  const fetchUsers = () => axios.get(`${API}/admin/users`).then(r => setUsers(r.data)).catch(() => setUsers([]));
  useEffect(() => { fetchUsers(); }, []);

  const togglePro = async (u) => {
    try {
      const r = await axios.post(`${API}/admin/users/${u.user_id}/toggle-pro`);
      toast.success(r.data.message);
      fetchUsers();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };

  const toggleBundle = async (u) => {
    try {
      const r = await axios.post(`${API}/admin/users/${u.user_id}/toggle-bundle`);
      toast.success(r.data.message);
      fetchUsers();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };

  if (!users) return <div className="p-6 flex justify-center"><Loader2 className="h-6 w-6 animate-spin text-primary" /></div>;

  return (
    <div className="p-4 sm:p-6 max-w-4xl mx-auto">
      <p className="text-sm text-muted-foreground mb-4">{users.length} registered users</p>
      <div className="space-y-2">
        {users.map(u => (
          <div key={u.user_id} className="bg-card border rounded-xl p-3.5 flex items-center gap-3" data-testid={`admin-user-${u.user_id}`}>
            {u.picture ? <img src={u.picture} alt="" className="w-9 h-9 rounded-full" /> : <Avatar name={u.name || u.email || 'U'} size="md" />}
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2 flex-wrap">
                <span className="font-medium text-sm truncate">{u.name || '—'}</span>
                <Badge variant="outline" className={`text-xs ${PLAN_STYLE[u.plan] || PLAN_STYLE.free}`}>{u.plan}</Badge>
                {u.has_bundle && <Badge variant="outline" className="text-xs bg-violet-500/10 text-violet-600 border-violet-200" data-testid={`bundle-badge-${u.user_id}`}>+CRM</Badge>}
              </div>
              <p className="text-xs text-muted-foreground truncate">{u.email}</p>
            </div>
            <div className="text-right text-xs text-muted-foreground shrink-0 hidden sm:block">
              <p>Joined {u.created_at ? new Date(u.created_at).toLocaleDateString() : '—'}</p>
              <p>Active {u.last_active ? new Date(u.last_active).toLocaleDateString() : 'never'}</p>
            </div>
            {u.plan !== 'creator' && (u.plan === 'free' || u.comped) && (
              <div className="flex gap-1.5 shrink-0">
                <Button size="sm" variant={u.comped ? "outline" : "default"} className="text-xs h-8" onClick={() => togglePro(u)} data-testid={`toggle-pro-${u.user_id}`}>
                  <Crown className="h-3.5 w-3.5 mr-1" />{u.comped ? "Revoke Pro" : "Grant Pro"}
                </Button>
                <Button size="sm" variant={u.has_bundle ? "outline" : "secondary"} className={`text-xs h-8 ${u.has_bundle ? '' : 'bg-violet-600 hover:bg-violet-700 text-white'}`} onClick={() => toggleBundle(u)} data-testid={`toggle-bundle-${u.user_id}`}>
                  <Handshake className="h-3.5 w-3.5 mr-1" />{u.has_bundle ? "Revoke CRM" : "Grant CRM"}
                </Button>
              </div>
            )}
          </div>
        ))}
      </div>
    </div>
  );
};
